import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { isType } from '@sorare/core/src/lib/gql';
import type { Stats } from '@sorare/core/src/lib/scoring';

import { Grade } from '@sorare/marketplace/src/components/player/Grade';
import { Cell } from '@sorare/marketplace/src/components/searchPlayerTable/SearchTable/ui';

import type {
  PlayerScoreStats,
  PlayerScoreView,
} from '#src/components/fixture/FixtureTopPlayersTable/types.ts';
import {
  AnySportPlayerScoreStats,
  FootballPlayerScoreStats,
  PlayerScoreViewAttributes,
} from '#src/components/fixture/FixtureTopPlayersTable/types.ts';

import type { FootballScoreCells_fixturePlayer } from './__generated__/index.graphql.ts';

const ScoreCells = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  padding: var(--unit) 0;
  row-gap: var(--unit);
`;

const scoreShapeByMode: {
  [key in PlayerScoreStats]?: 'PILL' | 'TEXT' | 'HEXAGON';
} = {
  [AnySportPlayerScoreStats.SCORE]: 'PILL',
  [FootballPlayerScoreStats.DS]: 'TEXT',
  [FootballPlayerScoreStats.AA]: 'TEXT',
};

type FootballScoreCellsProps = {
  eligibleSo5Scores: FootballScoreCells_fixturePlayer['eligibleSo5Scores'];
  statsManager: PlayerScoreView;
  isUpcomingFixture?: boolean;
};

export const FootballScoreCells = ({
  eligibleSo5Scores,
  statsManager,
  isUpcomingFixture,
}: FootballScoreCellsProps) => {
  return (
    <ScoreCells
      style={{
        gridColumn: `2 / ${statsManager[PlayerScoreViewAttributes.PLAYER_SCORE].length + 2}`,
      }}
    >
      {eligibleSo5Scores.map(so5Score => {
        if (!isType(so5Score, 'PlayerGameScore')) {
          return null;
        }

        const { score, scoreStatus } = so5Score || {};
        const allAroundStats = so5Score?.allAroundStats || [];
        const positiveDecisiveStats = so5Score?.positiveDecisiveStats || [];
        const decisiveScore = so5Score?.decisiveScore?.totalScore || 0;
        const allAroundScore = allAroundStats.reduce(
          (acc, value) => acc + value.totalScore,
          0
        );

        const playerScoreStats = {
          [AnySportPlayerScoreStats.SCORE]: score,
          [FootballPlayerScoreStats.DS]: decisiveScore,
          [FootballPlayerScoreStats.AA]: allAroundScore,
        };

        return statsManager[PlayerScoreViewAttributes.PLAYER_SCORE].map(
          stat => (
            <Cell key={stat}>
              {stat === FootballPlayerScoreStats.DS && (
                <Horizontal gap={0.5}>
                  {positiveDecisiveStats
                    .filter(
                      positiveDecisiveStat =>
                        positiveDecisiveStat.statValue !== 0
                    )
                    .map(positiveDecisiveStat => (
                      <StatIcon
                        key={positiveDecisiveStat.stat}
                        statKey={positiveDecisiveStat.stat as Stats}
                      />
                    ))}
                </Horizontal>
              )}
              {isUpcomingFixture && stat === AnySportPlayerScoreStats.SCORE ? (
                <>
                  {!!so5Score?.projection && (
                    <Grade projection={so5Score?.projection} />
                  )}
                </>
              ) : (
                <>
                  {!!scoreStatus && (
                    <Score
                      hideReviewing
                      shape={scoreShapeByMode[stat]}
                      score={
                        playerScoreStats[stat as FootballPlayerScoreStats] || 0
                      }
                      scoreStatus={scoreStatus}
                      scoreType={
                        stat === FootballPlayerScoreStats.AA
                          ? 'ALL_AROUND_SCORE'
                          : 'PLAYER_SCORE'
                      }
                      animated={scoreShapeByMode[stat] === 'PILL'}
                      size={4}
                    />
                  )}
                </>
              )}
            </Cell>
          )
        );
      })}
    </ScoreCells>
  );
};

FootballScoreCells.fragments = {
  fixturePlayer: gql`
    fragment FootballScoreCells_fixturePlayer on FixturePlayer {
      id
      eligibleSo5Scores {
        id
        ... on PlayerGameScore {
          id
          score
          scoreStatus
          allAroundStats {
            totalScore
          }
          positiveDecisiveStats {
            stat
            statValue
          }
          decisiveScore {
            totalScore
          }
          projection {
            id
            ...Grade_playerGameScoreProjection
          }
        }
      }
    }
    ${Grade.fragments.projection}
  ` as TypedDocumentNode<FootballScoreCells_fixturePlayer>,
};
